import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { icons } from './ui/Icons';
import { useAppContext } from '../context/AppContext';
import { logoutUser } from '../services/authService';
import NotificationCenter from './NotificationCenter';
import Button from './ui/Button';
import toast from 'react-hot-toast';

const Header = () => {
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Helper: obter barbershopId do owner (suporta formato antigo e novo)
  const getOwnerBarbershopId = () => {
    if (!currentUser || currentUser.role !== 'owner') return null;
    // Formato antigo: string barbershopId
    if (currentUser.barbershopId) return currentUser.barbershopId;
    // Formato novo: array barbershops - pegar a primeira
    if (currentUser.barbershops && currentUser.barbershops.length > 0) {
      return currentUser.barbershops[0];
    }
    return null;
  };

  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      toast.success(result.message);
      navigate('/');
    } else {
      toast.error(result.error);
    }
  };

  // Se não tiver usuário logado, mostrar Header simplificado
  if (!currentUser) {
    return (
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <nav className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-2">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink min-w-0">
              <div
                className="flex-shrink-0 flex items-center cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img src="/barberize.png" alt="Barberize" className="h-7 sm:h-8 w-auto" />
                <span className="ml-1 sm:ml-2 text-base sm:text-xl md:text-2xl font-bold text-gray-900 truncate">Barberize</span>
              </div>
              
              {/* Link de Contato - Oculto em telas muito pequenas, visível em sm+ */}
              <button
                onClick={() => navigate('/contato')}
                className="hidden xs:block text-gray-700 hover:text-indigo-600 text-xs sm:text-sm font-medium whitespace-nowrap"
              >
                Contato
              </button>
              <button
                onClick={() => navigate('/suporte')}
                className="hidden xs:block text-gray-700 hover:text-indigo-600 text-xs sm:text-sm font-medium whitespace-nowrap"
              >
                Ajuda
              </button>
            </div>

            {/* Botões de Login/Registro */}
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0">
              <Button
                variant="secondary"
                onClick={() => navigate('/login')}
                className="py-1.5 px-2 sm:px-3 md:py-2 md:px-4 text-xs sm:text-sm whitespace-nowrap"
              >
                Entrar
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/registro')}
                className="py-1.5 px-2 sm:px-3 md:py-2 md:px-4 text-xs sm:text-sm whitespace-nowrap"
              >
                Cadastrar
              </Button>
            </div>
          </div>
        </nav>
      </header>
    );
  }

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div
            className="flex-shrink-0 flex items-center cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/barberize.png" alt="Barberize" className="h-8 w-auto" />
            <span className="ml-2 text-2xl font-bold text-gray-900">Barberize</span>
          </div>

          {/* Links Desktop */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
            >
              Início
            </button>
            
            <button
              onClick={() => navigate('/contato')}
              className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
            >
              Contato
            </button>
            
            <button
              onClick={() => navigate('/suporte')}
              className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
            >
              Ajuda
            </button>
            
            {currentUser?.role === 'user' && (
              <button
                onClick={() => navigate('/meus-agendamentos')}
                className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
              >
                Meus Agendamentos
              </button>
            )}
            {currentUser?.role === 'owner' && (
              <button
                onClick={() => {
                  const barbershopId = getOwnerBarbershopId();
                  if (barbershopId) navigate(`/barbershop/${barbershopId}`);
                }}
                className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
              >
                Minha Barbearia
              </button>
            )}
            {currentUser?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
              >
                Painel Admin
              </button>
            )}

            {/* Notification Center */}
            <NotificationCenter userId={currentUser.id} />
            
            {/* User Info e Logout */}
            <div className="flex items-center space-x-3 border-l border-gray-200 pl-4 ml-4">
              {icons.user({ className: 'h-6 w-6 text-gray-600' })}
              <button
                onClick={() => navigate('/perfil')}
                className="text-sm font-medium text-gray-800 hover:text-indigo-600 transition-colors"
              >
                {currentUser.name}
              </button>
              <Button
                variant="secondary"
                onClick={handleLogout}
                className="py-1.5 px-3 text-sm"
              >
                Sair
              </Button>
            </div>
          </div>

          {/* Botão Menu Mobile */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Notification Center Mobile */}
            <NotificationCenter userId={currentUser.id} />
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              {isMobileMenuOpen ? icons.x({ className: 'h-6 w-6' }) : icons.menu({ className: 'h-6 w-6' })}
            </button>
          </div>
        </div>
      </nav>

      {/* Menu Mobile */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <button
              onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}
              className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
            >
              Início
            </button>
            
            <button
              onClick={() => { navigate('/contato'); setIsMobileMenuOpen(false); }}
              className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
            >
              Contato
            </button>
            
            <button
              onClick={() => { navigate('/suporte'); setIsMobileMenuOpen(false); }}
              className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
            >
              Ajuda
            </button>
            
            {currentUser?.role === 'user' && (
              <button
                onClick={() => { navigate('/meus-agendamentos'); setIsMobileMenuOpen(false); }}
                className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
              >
                Meus Agendamentos
              </button>
            )}
             {currentUser?.role === 'owner' && (
              <button
                onClick={() => {
                  const barbershopId = getOwnerBarbershopId();
                  if (barbershopId) {
                    navigate(`/barbershop/${barbershopId}`);
                    setIsMobileMenuOpen(false);
                  }
                }}
                className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
              >
                Minha Barbearia
              </button>
            )}
            {currentUser?.role === 'admin' && (
              <button
                onClick={() => { navigate('/admin'); setIsMobileMenuOpen(false); }}
                className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
              >
                Painel Admin
              </button>
            )}
            
            <div className="border-t border-gray-200 pt-4 pb-3">
              <button
                onClick={() => { navigate('/perfil'); setIsMobileMenuOpen(false); }}
                className="flex items-center px-5 w-full hover:bg-gray-50 py-2 rounded transition-colors"
              >
                {icons.user({ className: 'h-8 w-8 text-gray-600' })}
                <div className="ml-3 text-left">
                  <div className="text-base font-medium text-gray-800">{currentUser.name}</div>
                  <div className="text-sm font-medium text-gray-500">{currentUser.email}</div>
                </div>
              </button>
              <div className="mt-3 px-2">
                <Button
                  variant="secondary"
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                  className="w-full py-2"
                >
                  Sair
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;